import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Modal from "../../Elements/Modal/Modal";
import MainButton from "../../Elements/MainButton/MainButton";
import SecondButton from "../../Elements/SecondButton/SecondButton";
import { fetchInternalPackage, fetchPackageById } from "../../../services/PackagesService";
import trashIcon from "../../../assets/icons/common/trash.png";
import { formatPackageName } from "../../../utils/packageName";
import "./AddedToCartModal.css";

const AddedToCartModal = ({
  isOpen,
  onClose,
  onGoToCart,
  onRemoveItem,
  items = [],
  currency: currentCurrency = "EUR",
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage || i18n.language || "en";
  const normalizedCurrency = String(currentCurrency || "EUR").toUpperCase();

  const [details, setDetails] = useState({});

  const detailsKey = (id) => `${id}|${currentLang}|${normalizedCurrency}`;

  const currency = (value, cur = "USD") => {
    if (value == null) return "";
    return `${Number(value).toFixed(2)} ${cur}`;
  };

  // Details are prefetched in the background, so the stored values are only a fallback
  const resolveItem = (item) => {
    const itemId = String(item.internal_package_id || item.id);
    const cached = details[detailsKey(itemId)];
    return {
      ...item,
      name: cached?.name ?? item.name,
      price: cached?.price ?? item.price,
    };
  };

  const formatName = (name) => formatPackageName(name, t("common.unlimited", "Unlimited"));

  const sortedItems = [...items].map(resolveItem).sort((a, b) => (b.lastAddedAt || 0) - (a.lastAddedAt || 0));
  const grandTotal = sortedItems.reduce((sum, item) => sum + (Number(item.price) || 0) * (item.quantity || 1), 0);

  useEffect(() => {
    if (isOpen && items.length === 0) {
      onClose();
    }
  }, [isOpen, items.length, onClose]);

  useEffect(() => {
    if (items.length === 0) return;

    let isCancelled = false;

    const loadTranslations = async () => {
      try {
        const uniqueItems = Array.from(
          new Map(items.map((item) => [String(item.internal_package_id || item.id), item])).entries(),
        );
        const results = await Promise.all(
          uniqueItems.map(async ([pid, item]) => {
            try {
              const isTopUp = item.type === "topup" || item.is_topup;
              const data = isTopUp
                ? await fetchInternalPackage(pid, normalizedCurrency)
                : await fetchPackageById(pid, normalizedCurrency, currentLang);
              return { id: String(pid), name: data.name, price: data.price };
            } catch (err) {
              console.error(`Failed to refresh package ${pid} for ${normalizedCurrency}:`, err);
              return null;
            }
          }),
        );

        if (!isCancelled) {
          const fresh = {};
          results.filter(Boolean).forEach((res) => {
            fresh[detailsKey(res.id)] = {
              name: res.name,
              price: res.price != null ? Number(res.price) : undefined,
            };
          });
          setDetails((prev) => ({ ...prev, ...fresh }));
        }
      } catch (err) {
        console.error("Failed to load package details for the selected currency:", err);
      }
    };

    loadTranslations();

    return () => {
      isCancelled = true;
    };
  }, [items, normalizedCurrency, currentLang]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("offers.addedToCart") || "Added to cart"}
      actions={
        <>
          <SecondButton onClick={onClose}>{t("offers.continueShopping") || "Continue shopping"}</SecondButton>
          <MainButton onClick={onGoToCart}>{t("offers.viewCart") || "Go to cart"}</MainButton>
        </>
      }
    >
      <div className="added-to-cart-body">
        <p className="added-to-cart-text">
          {t("offers.addedToCartText") || "Your eSIM plan has been added to your cart."}
        </p>

        <div className="added-to-cart-list">
          {sortedItems.map((item) => {
            return (
              <div key={item.id} className="added-to-cart-item">
                <div className="added-to-cart-item__name">{formatName(item.name)}</div>

                <div className="added-to-cart-item__right">
                  <div className="added-to-cart-item__calc">
                    {item.quantity || 1} × {currency(item.price, normalizedCurrency)}
                  </div>

                  <button
                    type="button"
                    className="added-to-cart-item__remove"
                    onClick={() => onRemoveItem(item.id, item.currency)}
                    aria-label="Remove from cart"
                  >
                    <img src={trashIcon} alt="" className="added-to-cart-item__remove-icon" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="added-to-cart-summary">
          <span className="added-to-cart-summary__label">{t("cart.total") || "Total"}</span>
          <span className="added-to-cart-summary__value">{currency(grandTotal, normalizedCurrency)}</span>
        </div>
      </div>
    </Modal>
  );
};

AddedToCartModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onGoToCart: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  items: PropTypes.array,
  currency: PropTypes.string,
};

export default AddedToCartModal;
